import { useEffect, useState } from "react";
import { fetchRewardsCatalog, redeemReward, fetchKudosPoints, listEmployeeRedemptions } from "../lib/api";
import { Gift, Calendar, BookOpen, Ticket, UserCheck, Home, Award, Coffee, Heart, Loader2, CheckCircle2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const ICON_MAP = { calendar: Calendar, book: BookOpen, ticket: Ticket, "user-check": UserCheck, gift: Gift, home: Home, award: Award, coffee: Coffee, heart: Heart };

const CATEGORY_ORDER = ["Zaman", "Gelişim", "Deneyim", "Yaşam", "Etki"];

export default function RewardsCatalog({ employeeId, open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState(0);
  const [redemptions, setRedemptions] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    fetchRewardsCatalog().then((d) => setItems(d.items || []));
    fetchKudosPoints(employeeId).then((d) => setBalance(d.balance || 0));
    listEmployeeRedemptions(employeeId).then((d) => setRedemptions(d.redemptions || []));
  };
  useEffect(() => { if (open) reload(); }, [open, employeeId]);

  const handleRedeem = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      await redeemReward({ employee_id: employeeId, reward_id: confirming.id });
      toast.success(`${confirming.name} talebin İK'ya iletildi 🎉`);
      setConfirming(null);
      reload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kullanım sırasında hata oluştu.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const grouped = CATEGORY_ORDER
    .map((cat) => ({ category: cat, items: items.filter((i) => i.category === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto" data-testid="rewards-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-600" /> Ödül Kataloğu
            <span className="ml-auto text-sm font-normal text-slate-500">
              Bakiye: <span className="font-bold text-amber-700 text-base" data-testid="rewards-balance">{balance}</span> puan
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {grouped.map((g) => (
            <div key={g.category}>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{g.category}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {g.items.map((r) => (
                  <RewardCard key={r.id} reward={r} balance={balance} onRedeem={() => setConfirming(r)} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {redemptions.length > 0 && (
          <div className="border-t border-slate-200 pt-4 mt-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Geçmiş Kullanımlarım</div>
            <div className="space-y-1.5">
              {redemptions.slice(0, 5).map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium text-slate-800">{r.reward_name}</span>
                    <span className="text-xs text-slate-500">· {new Date(r.created_at).toLocaleDateString("tr-TR")}</span>
                  </div>
                  <span className="text-xs text-slate-500">-{r.points_cost}pt · <span className="capitalize">{r.status === "pending" ? "Onay bekliyor" : r.status === "approved" ? "Onaylandı" : r.status}</span></span>
                </div>
              ))}
            </div>
          </div>
        )}

        {confirming && (
          <div className="border-t border-slate-200 pt-4 mt-4 bg-amber-50 -mx-6 -mb-6 px-6 pb-6 rounded-b-lg">
            <div className="text-sm font-semibold text-amber-900 mb-2">Kullanımı onayla</div>
            <div className="text-sm text-slate-700">
              <span className="font-medium">{confirming.name}</span> için <span className="font-bold text-amber-700">{confirming.points_cost}</span> puan kullanılacak. Yeni bakiye: <span className="font-bold">{balance - confirming.points_cost}</span>.
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" size="sm" onClick={() => setConfirming(null)}><X className="w-3.5 h-3.5 mr-1" /> Vazgeç</Button>
              <Button size="sm" onClick={handleRedeem} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="rewards-confirm">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                Onayla ve kullan
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RewardCard({ reward, balance, onRedeem }) {
  const Icon = ICON_MAP[reward.icon] || Gift;
  const affordable = balance >= reward.points_cost;
  return (
    <div className={`rounded-xl border p-3.5 flex flex-col ${affordable ? "border-amber-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"}`} data-testid={`reward-card-${reward.id}`}>
      <div className="flex items-start gap-2.5 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${affordable ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 leading-tight">{reward.name}</div>
          <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{reward.description}</div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-100">
        <span className={`text-sm font-bold ${affordable ? "text-amber-700" : "text-slate-500"}`}>{reward.points_cost} pt</span>
        <Button size="sm" disabled={!affordable} onClick={onRedeem} className="h-7 text-xs" data-testid={`reward-redeem-${reward.id}`}>
          {affordable ? "Kullan" : `${reward.points_cost - balance} pt eksik`}
        </Button>
      </div>
    </div>
  );
}
